import { useContext, useRef, useState } from 'react';
import { Section } from '../templates/Section/Section';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { OverflowMenu } from '../atoms/OverflowMenu/OverflowMenu';
import { Text } from '../atoms/Typography/Text';
import { ConfirmDialog } from '../ui/ConfirmDialog/ConfirmDialog';
import { useTranslation } from '../../i18n/useTranslation';
import { ActiveAquariumContext } from '../../contexts/AquariumContext';
import { useOptionalCapture } from './CaptureContext';
import './OfflineCaptureQueue.css';

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Notes recorded offline or whose upload failed (wireframe "Offline Capture
 * Queue"), at the top of To review — proof that the note exists. Renders
 * nothing when the queue is empty (and storage works).
 */
export function OfflineCaptureQueue() {
  const { t, i18n } = useTranslation();
  const capture = useOptionalCapture();
  const aquariums = useContext(ActiveAquariumContext)?.aquariums ?? [];
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const listRef = useRef<HTMLUListElement>(null);

  if (!capture) return null;
  const { queue } = capture;
  if (queue.length === 0) {
    return capture.storageAvailable || !capture.available ? null : (
      <Alert tone="warning" compact live="off">{t('voice.queue.storageUnavailable')}</Alert>
    );
  }

  const tankName = (id: string): string => aquariums.find((a) => a.id === id)?.name ?? '';
  const confirmDiscard = (): void => {
    if (!confirmId) return;
    const index = queue.findIndex((n) => n.id === confirmId);
    capture.discardQueued(confirmId);
    setConfirmId(null);
    // Focus the next row (or the section's first action) after the confirmation.
    setTimeout(() => {
      const rows = listRef.current?.querySelectorAll<HTMLElement>('li button');
      rows?.[Math.min(index, Math.max(0, (rows?.length ?? 1) - 1))]?.focus();
    }, 0);
  };

  return (
    <Section
      title={t('voice.queue.title')}
      actions={
        <Button variant="secondary" onClick={() => capture.retryQueued()}>
          {t('voice.queue.retryAll')}
        </Button>
      }
      data-testid="offline-capture-queue"
    >
      <ul className="offline-queue__list" ref={listRef}>
        {queue.map((note) => (
          <li key={note.id} className="offline-queue__item">
            <div className="offline-queue__text">
              <Text as="span" weight="medium">
                {t('voice.queue.itemRecordedAt', {
                  dateTime: new Date(note.recordedAt).toLocaleString(i18n.language, { dateStyle: 'short', timeStyle: 'short' }),
                })}
              </Text>
              <Text as="span" variant="caption" color="muted">
                {t('voice.queue.itemDuration', { duration: formatDuration(note.durationMs), tank: tankName(note.aquariumId) })}
              </Text>
            </div>
            {note.status === 'failed' ? (
              <>
                <Badge tone="warning">{t('voice.queue.statusFailed')}</Badge>
                <Button variant="secondary" size="sm" onClick={() => capture.retryQueued(note.id)}>
                  {t('voice.queue.retry')}
                </Button>
              </>
            ) : (
              <Badge tone="neutral">{t('voice.queue.statusWaiting')}</Badge>
            )}
            <OverflowMenu
              label={String(t('voice.queue.itemMore'))}
              items={[{ id: 'discard', label: String(t('voice.queue.discard')), tone: 'danger', onSelect: () => setConfirmId(note.id) }]}
            />
          </li>
        ))}
      </ul>
      {!capture.storageAvailable && (
        <Alert tone="warning" compact live="off">{t('voice.queue.storageUnavailable')}</Alert>
      )}
      <ConfirmDialog
        isOpen={confirmId !== null}
        title={String(t('voice.queue.discardTitle'))}
        message={String(t('voice.queue.discardConfirm'))}
        confirmLabel={String(t('voice.queue.discard'))}
        cancelLabel={String(t('common.cancel'))}
        onConfirm={confirmDiscard}
        onCancel={() => setConfirmId(null)}
      />
    </Section>
  );
}
